import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import { FilterData, ListingTableColumn, OrderData } from 'common/components/listingTable/models';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import PageContainer from 'common/components/pageContainer/PageContainer';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageHeader from 'common/components/pageHeader/PageHeader';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import Toast from 'common/services/Toast';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { NumerationDto } from 'api/numerations/models/NumerationDto';
import { NumerationsSearchCriteria } from 'api/numerations/models/NumerationsSearchCriteria';
import NumerationsService from 'api/numerations/NumerationsService';
import { NumerationType } from 'api/numerations/enums/NumerationType';

const PAGE_ID = 'BACKOFFICE_NUMERATIONSLIST';

function NumerationsList(): JSX.Element | null {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<ListingTableData<NumerationDto, Partial<NumerationsSearchCriteria>>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'numerationType', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null,
    }));
    const [numerations, setNumerations] = useState<NumerationDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0)
    const [refreshKey, setRefreshKey] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['NUMERATIONS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['NUMERATIONS_READ'])
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);
    const typesOptions = useMemo(() => Object.keys(NumerationType).map(key => ({
        label: t('numerations.types.' + key),
        value: key,
    })), [i18n.language]);

    const columns: ListingTableColumn<NumerationDto>[] = orderColumns([
        {
            name: t('numerations.list.type'),
            field: 'numerationType',
            orderColumn: 'numeration_type',
            filter: { type: 'dropdown', value: tableData.filters.numerationType ?? '', options: typesOptions || [] },
            minWidth: '18rem',
            renderCell: (row) => {
                return t('numerations.types.' + row.numerationType);
            }
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDateStart ? new Date(tableData.filters.updatedDateStart) : null,
                    end: tableData.filters.updatedDateEnd ? new Date(tableData.filters.updatedDateEnd) : null
                }
            },
            minWidth: '22rem',
            orderColumn: 'updated_date',
            renderCell: (row) => {
                return row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '';
            }
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '21rem',
            orderColumn: 'user_updated_name',
        },
    ], tableData.columnsOrder);

    const renderAction = (row: NumerationDto) => (
        <TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, onClick: () => navigateTo('edit',row.id) },
            ]}
        />
    )

    const onFilter = (col: ListingTableColumn<NumerationDto>, filterData: FilterData) => {
        setTableData(d => {
            const data = ({
                ...d, page: 1
            })
            if (col.field === 'numerationType') {
                data.filters = {
                    ...data.filters,
                    numerationType: filterData.value,
                };
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date
                };
            }

            if (col.field === 'userUpdatedName') {
                data.filters = {
                    ...data.filters,
                    userUpdatedName: filterData.value as string
                };
            }
            return data;
        });
        setRefreshKey(k => k + 1);
    }

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1);
    }

    const onOrder = (orderData: OrderData<NumerationDto>) => {
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
        setRefreshKey(k => k + 1);
    }

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    const getData = async () => {
        try {
            if (isLoading) {
                return;
            }

            Loading.show();
            setIsLoading(true);
            const data = await NumerationsService.getList({
                ...tableData.filters,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc'
            });

            const newListElements = data.currentPage === 1 ? data.items : [...numerations, ...data.items]
            setHasMore(data.totalItems > newListElements.length);
            setNumerations(newListElements);
            setTotalItems(data.totalItems)

            if (firstLoad.current) {
                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get numerations list', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setIsLoading(false);
        }
    }

    const navigateTo = (type: string,id?: string) => {
        if (type === 'create') {
            navigate(`/backoffice/numerations/${type}`);
        } else if (id) {
            ListingTableCache.saveLastRowId(PAGE_ID, id);
            navigate(`/backoffice/numerations/${type}/${id}`);
        }
    }

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    }

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('numerations.list.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('numerations.list.title'), url: '/backoffice/numerations' },
                ]}
            />
            <PageHeader title={t('numerations.list.title')} subTitle={`${totalItems} ${t('common.total_results')}`} informationText={t('common.list')} showGoBack={false} addSidebarSpacing />
            <PageContainer>
                <ListingTable<NumerationDto>
                    columns={columns}
                    rows={numerations}
                    order={tableData.order}
                    hiddenColumns={tableData.hiddenColumns}
                    columnsWidths={tableData.columnsWidth}
                    onOrder={onOrder}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                    onRenderAction={canWrite ? renderAction : undefined}
                    onRowClick={(row) => navigateTo('details', row.id)}
                    infiniteScroll={{
                        isLoading,
                        hasMore,
                        onLoadMore,
                    }}
                    onRowId={r => r.id}
                    scrollToRowId={scrollToRowId}
                    onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
                />
            </PageContainer>
            <ListingTableSidebar
                columns={columns}
                hiddenColumns={tableData.hiddenColumns}
                onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
                onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
            />
        </ScreenTitle>
    );
}

export default NumerationsList;
